import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import "./people.css";
import "./team-onboarding.css";
import { getEmployee, getComplianceOverview } from "../../api/teamApi";
import { listSops } from "../../api/sopsApi";
import {
  listOnboardingTemplates, createOnboardingTemplate, updateOnboardingTemplate, deleteOnboardingTemplate,
  listOnboardingAssignments, assignOnboarding, sendOnboarding, completeManagerStep,
  requestDocument, resendDocumentRequest,
  STEP_KINDS, TEMPLATE_ROLES, DOC_TYPES, docTypeLabel, templateRoleFor, defaultTemplates,
} from "../../api/onboardingApi";
import { getUserRole } from "../../helpers/tenantHelpers";

/* Team → Onboarding — a tool, not a checklist with "Mark done".
   - Checklists per role, built from a template (tech / office / sales …), editable.
   - Start = assign the role's checklist + send the employee a link; on their
     phone they upload the I-9, W-4, license, and read + sign the handbook.
   - Request a document from anyone with one click; they get the link; it lands
     in their Docs tab. Assign an SOP to read and sign the same way.
   - The manager sees who's stuck on what, for how long, and every completion
     is a real artifact (file, signature, credential, training record). */

const MANAGER_ROLES = new Set(["owner", "admin", "manager"]);
const KIND_LABEL = Object.fromEntries(STEP_KINDS.map((k) => [k.value, k.label]));
const ago = (iso) => { if (!iso) return null; const d = Math.floor((Date.now() - new Date(iso)) / 86400000); return d <= 0 ? "today" : d === 1 ? "1 day ago" : `${d} days ago`; };
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");
const notifiedText = (n) => {
  if (!n) return "";
  const parts = [];
  if (n.email) parts.push(n.email === "sent" ? "emailed" : n.email === "no_address" ? "no email on file" : `email ${n.email}`);
  if (n.push) parts.push(n.push === "sent" ? "pushed to their phone" : n.push === "no_device" ? "no app device" : `push ${n.push}`);
  return parts.join(" · ");
};
const copy = async (text) => { try { await navigator.clipboard.writeText(text); toast.success("Link copied"); } catch { window.prompt("Copy this link", text); } };
const absolute = (path) => `${window.location.origin}${path}`;

export default function TeamOnboarding({ openEmployee }) {
  const role = String(getUserRole("Member") || "").toLowerCase();
  const isManager = MANAGER_ROLES.has(role);
  const [data, setData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [sops, setSops] = useState([]);
  const [credTypes, setCredTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notDeployed, setNotDeployed] = useState(false);
  const [error, setError] = useState(null);
  const [panel, setPanel] = useState(null); // 'checklists' | 'request' | null
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(null);
  const [joins, setJoins] = useState({}); // employee_id → person join (credentials for linking)
  const [starting, setStarting] = useState(null); // employee_id picking a template

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [a, t] = await Promise.all([listOnboardingAssignments(), listOnboardingTemplates()]);
      setData(a.data); setTemplates(t.data?.templates || []); setNotDeployed(false);
    } catch (e) {
      if (e?.status === 404) setNotDeployed(true);
      else if (e?.status === 403) setError("Only admins and managers can run onboarding.");
      else setError(e?.payload?.detail || "Couldn’t load onboarding.");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    listSops().then((r) => setSops((r.data?.sops || []).filter((s) => s.is_published !== false))).catch(() => {});
    getComplianceOverview().then((r) => { const b = r.data?.requirements || {}; const reqs = Array.isArray(b) ? b : b.requirements || []; setCredTypes(reqs.filter((q) => q.holder_level === "employee")); }).catch(() => {});
  }, []);

  const rows = data?.employees || [];
  const templateFor = useCallback((emp) => {
    const want = templateRoleFor(emp.role);
    return templates.find((t) => t.role === want) || templates.find((t) => t.role === "general") || templates[0] || null;
  }, [templates]);

  /* ── actions ── */
  const start = async (emp, template) => {
    if (!template) { setPanel("checklists"); toast.info("Build a checklist first."); return; }
    setBusy(`start-${emp.employee_id}`);
    try {
      const a = await assignOnboarding(emp.employee_id, template.id);
      const s = await sendOnboarding(a.data.id, {});
      const n = notifiedText(s.data?.notified);
      toast.success(`${emp.name}: checklist sent${n ? ` — ${n}` : ""}`);
      setStarting(null); await load(); setOpen(emp.employee_id);
    } catch (e) { toast.error(e?.payload?.detail || "Couldn’t start onboarding."); }
    finally { setBusy(null); }
  };
  const resend = async (emp) => {
    setBusy(`send-${emp.employee_id}`);
    try { const s = await sendOnboarding(emp.assignment.id, {}); toast.success(`Link sent — ${notifiedText(s.data?.notified) || "logged"}`); await load(); }
    catch { toast.error("Couldn’t send the link."); }
    finally { setBusy(null); }
  };
  const completeStep = async (emp, step, form) => {
    setBusy(`step-${step.id}`);
    try { await completeManagerStep(emp.assignment.id, step.id, form); toast.success(`Done: ${step.label}`); await load(); }
    catch (e) { toast.error(e?.payload?.detail || "Couldn’t complete that step."); }
    finally { setBusy(null); }
  };
  const expand = async (emp) => {
    const next = open === emp.employee_id ? null : emp.employee_id;
    setOpen(next);
    if (next && !joins[emp.employee_id]) {
      try { const j = await getEmployee(emp.employee_id); setJoins((m) => ({ ...m, [emp.employee_id]: j.data })); } catch { /* optional */ }
    }
  };

  if (!isManager) return <div className="team-tab"><section className="ppl-card ppl-card--wide"><div className="ppl-muted">Onboarding is run by admins and managers. Your own checklist arrives as a link.</div></section></div>;

  return (
    <div className="team-tab ob">
      <section className="ppl-card ppl-card--wide">
        <div className="ppl-row-head">
          <h3>Onboarding{data ? <span className="ppl-count">{data.in_progress} in progress · {data.not_started} not started</span> : null}</h3>
          <div className="ppl-actions">
            <button type="button" className={`ops-btn secondary ${panel === "request" ? "is-on" : ""}`} onClick={() => setPanel(panel === "request" ? null : "request")}>Request a document</button>
            <button type="button" className={`ops-btn secondary ${panel === "checklists" ? "is-on" : ""}`} onClick={() => setPanel(panel === "checklists" ? null : "checklists")}>Checklists{templates.length ? ` · ${templates.length}` : ""}</button>
          </div>
        </div>

        {notDeployed && <div className="ppl-note danger">The onboarding workflow isn’t deployed on the backend yet (lane D’s <code>/api/people/onboarding/assignments/</code> is 404). This page lights up on the next backend deploy — nothing here is sample data.</div>}
        {error && <div className="ppl-note danger">{error}</div>}

        {panel === "request" && <RequestDocumentPanel rows={rows} onDone={() => { setPanel(null); load(); }} />}
        {panel === "checklists" && (
          <ChecklistsPanel templates={templates} sops={sops} credTypes={credTypes} onChange={async () => { const t = await listOnboardingTemplates(); setTemplates(t.data?.templates || []); }} />
        )}

        {loading ? <div className="ppl-muted">Loading…</div> : !notDeployed && rows.length === 0 ? (
          <div className="ppl-empty"><strong>No employees on the roster.</strong><span>Add people in the Directory; each gets the checklist for their role.</span></div>
        ) : !notDeployed && (
          <div className="ppl-table-wrap">
            <table className="ppl-table ob-table">
              <thead><tr><th>Person</th><th>Role</th><th>Checklist</th><th>Progress</th><th>Stuck on</th><th>Link</th><th /></tr></thead>
              <tbody>
                {rows.map((emp) => {
                  const a = emp.assignment;
                  const isOpen = open === emp.employee_id;
                  const pct = a && a.total ? Math.round((a.done / a.total) * 100) : 0;
                  const tmpl = templateFor(emp);
                  return (
                    <React.Fragment key={emp.employee_id}>
                      <tr className={isOpen ? "is-open" : ""}>
                        <td className="nowrap"><button type="button" className="ppl-link ppl-name" onClick={() => openEmployee(emp.user_id, "docs")}>{emp.name}</button>{emp.open_requests?.length ? <span className="ppl-sub">{emp.open_requests.length} document request{emp.open_requests.length === 1 ? "" : "s"} open</span> : null}</td>
                        <td className="muted nowrap">{String(emp.role || "").replace(/_/g, " ") || "—"}</td>
                        <td className="nowrap">{a ? (a.template?.name || "Checklist") : <span className="muted">—</span>}</td>
                        <td>{a ? <div className="ob-progress"><div className={`ppl-bar ${a.status === "complete" ? "is-done" : ""}`}><span style={{ width: `${pct}%` }} /></div><span className="ppl-sub">{a.done}/{a.total}</span></div> : <span className="ppl-pill">not started</span>}</td>
                        <td>{a ? a.status === "complete" ? <span className="ppl-pill done">complete</span> : a.stuck_on ? <span className="ob-stuck"><span>{a.stuck_on.label}</span><span className="ppl-sub">{a.stuck_on.assignee === "manager" ? "manager step" : "waiting on them"}{a.stuck_on.days ? ` · ${a.stuck_on.days} day${a.stuck_on.days === 1 ? "" : "s"}` : ""}</span></span> : "—" : <span className="muted">—</span>}</td>
                        <td className="nowrap">{a ? <span className="ob-link">{a.last_notified_at ? <span className="ppl-sub">sent {ago(a.last_notified_at)}</span> : <span className="ppl-sub">never sent</span>}<button type="button" className="ppl-link" onClick={() => copy(absolute(a.self_url))}>Copy</button><button type="button" className="ppl-link" disabled={busy === `send-${emp.employee_id}`} onClick={() => resend(emp)}>Resend</button></span> : <span className="muted">—</span>}</td>
                        <td className="nowrap ob-actions">
                          {a ? <button type="button" className="ops-btn secondary" onClick={() => expand(emp)}>{isOpen ? "Hide" : "Steps"}</button>
                            : starting === emp.employee_id ? (
                              <span className="ob-start">
                                <select defaultValue={tmpl?.id || ""} id={`tmpl-${emp.employee_id}`}>{templates.map((t) => <option key={t.id} value={t.id}>{t.name} · {TEMPLATE_ROLES.find((r) => r.value === t.role)?.label || t.role}</option>)}</select>
                                <button type="button" className="ops-btn" disabled={busy === `start-${emp.employee_id}`} onClick={() => { const id = Number(document.getElementById(`tmpl-${emp.employee_id}`).value); start(emp, templates.find((t) => t.id === id)); }}>Send</button>
                                <button type="button" className="ppl-link" onClick={() => setStarting(null)}>Cancel</button>
                              </span>
                            ) : <button type="button" className="ops-btn" onClick={() => (templates.length > 1 ? setStarting(emp.employee_id) : start(emp, tmpl))}>Start{tmpl ? ` · ${tmpl.name}` : ""}</button>}
                        </td>
                      </tr>
                      {isOpen && a && (
                        <tr className="ob-detail"><td colSpan={7}>
                          <StepList emp={emp} join={joins[emp.employee_id]} busy={busy} onComplete={(step, form) => completeStep(emp, step, form)} openEmployee={openEmployee} />
                          {emp.open_requests?.length > 0 && (
                            <div className="ob-requests">
                              <div className="ob-sub-title">Open document requests</div>
                              {emp.open_requests.map((r) => (
                                <div key={r.id} className="ppl-list-row"><div className="ppl-list-main"><span>{r.label}</span><span className="ppl-sub">asked {ago(r.requested_at)}{r.note ? ` · ${r.note}` : ""}</span></div>
                                  <button type="button" className="ppl-link" onClick={() => copy(absolute(r.upload_url))}>Copy link</button>
                                  <button type="button" className="ppl-link" onClick={async () => { try { const s = await resendDocumentRequest(r.id); toast.success(`Reminder sent — ${notifiedText(s.data?.notified)}`); } catch { toast.error("Couldn’t resend."); } }}>Remind</button>
                                </div>
                              ))}
                            </div>
                          )}
                        </td></tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

/* ── the steps on one person's checklist, with the real artifacts ── */
function StepList({ emp, join, busy, onComplete, openEmployee }) {
  const a = emp.assignment;
  const creds = join?.credentials || [];
  return (
    <div className="ob-steps">
      {a.steps.map((s) => {
        const done = s.status === "done";
        const mine = s.assignee === "manager";
        return (
          <div key={s.id} className={`ob-step ${done ? "is-done" : ""}`}>
            <span className={`ob-dot ${done ? "done" : mine ? "mgr" : ""}`} aria-hidden />
            <div className="ob-step-main">
              <span className="ob-step-label">{s.label}{s.required === false && <span className="ppl-sub"> · optional</span>}</span>
              <span className="ppl-sub">{KIND_LABEL[s.kind] || s.kind}{s.kind === "document_upload" && s.doc_type_label ? ` · ${s.doc_type_label}` : ""}{s.kind === "signature" && s.sop_title ? ` · ${s.sop_title}` : ""} · {mine ? "a manager does this" : "the employee does this"}</span>
              {done && <Artifact s={s} />}
            </div>
            <div className="ob-step-act">
              {done ? <span className="ppl-pill done">done{s.completed_at ? ` · ${fmt(s.completed_at)}` : ""}</span>
                : !mine ? <span className="ppl-pill open">waiting on them</span>
                : s.kind === "document_upload" ? (
                  <label className="ops-btn secondary ob-upload">{busy === `step-${s.id}` ? "Uploading…" : "Upload"}<input type="file" hidden disabled={busy === `step-${s.id}`} onChange={(e) => { const f = e.target.files?.[0]; if (!f) return; const form = new FormData(); form.append("file", f); onComplete(s, form); e.target.value = ""; }} /></label>
                ) : s.kind === "training" ? (
                  <button type="button" className="ops-btn secondary" disabled={busy === `step-${s.id}`} onClick={() => { const form = new FormData(); form.append("course", s.config?.training_course || s.label); form.append("completed_on", new Date().toISOString().slice(0, 10)); onComplete(s, form); }}>Record completion</button>
                ) : s.kind === "credential" ? (
                  creds.length ? (
                    <span className="ob-start">
                      <select id={`cred-${s.id}`} defaultValue={(creds.find((c) => c.type_key === s.config?.credential_type_key) || creds[0]).id}>{creds.map((c) => <option key={c.id} value={c.id}>{c.label || c.type_key}{c.number ? ` #${c.number}` : ""}</option>)}</select>
                      <button type="button" className="ops-btn secondary" disabled={busy === `step-${s.id}`} onClick={() => { const form = new FormData(); form.append("credential_id", document.getElementById(`cred-${s.id}`).value); onComplete(s, form); }}>Link</button>
                    </span>
                  ) : <button type="button" className="ppl-link" onClick={() => openEmployee(emp.user_id, "compliance")}>Add the license from its PDF →</button>
                ) : s.kind === "signature" ? (
                  <span className="ppl-sub">only the signer can sign — resend the link</span>
                ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Artifact({ s }) {
  const a = s.artifact;
  if (!a) return null;
  if (a.type === "file") return <span className="ob-artifact">{a.url ? <a href={a.url} target="_blank" rel="noreferrer">{a.name || "file"}</a> : a.name}{a.size_bytes ? <span className="ppl-sub"> · {Math.max(1, Math.round(a.size_bytes / 1024))} KB</span> : null}</span>;
  if (a.type === "signature") return <span className="ob-artifact">Signed {a.typed_name ? `as “${a.typed_name}”` : a.method === "click_accept" ? "by click" : a.method?.replace(/_/g, " ")}{a.platform ? ` on ${a.platform}` : ""}{a.sop_version ? ` · v${a.sop_version}` : ""}{a.signed_at ? ` · ${fmt(a.signed_at)}` : ""}</span>;
  if (a.type === "credential") return <span className="ob-artifact">Credential #{a.id} linked</span>;
  if (a.type === "training") return <span className="ob-artifact">{a.course}{a.completed_on ? ` · ${fmt(a.completed_on)}` : ""}</span>;
  return null;
}

/* ── request a document from anyone ── */
function RequestDocumentPanel({ rows, onDone }) {
  const [form, setForm] = useState({ employee_id: "", doc_type: "drivers_license", note: "" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.employee_id) { toast.error("Pick who to ask."); return; }
    setSaving(true);
    try {
      const r = await requestDocument({ employee_id: Number(form.employee_id), doc_type: form.doc_type, note: form.note });
      const who = rows.find((x) => String(x.employee_id) === String(form.employee_id))?.name || "them";
      toast.success(`Asked ${who} for ${docTypeLabel(form.doc_type)} — ${notifiedText(r.data?.notified) || "link ready"}`);
      onDone();
    } catch (ex) { toast.error(ex?.payload?.detail || "Couldn’t send the request."); }
    finally { setSaving(false); }
  };
  return (
    <form className="ob-panel ppl-form" onSubmit={submit}>
      <label><span>Who</span><select value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })}><option value="">Select…</option>{rows.map((r) => <option key={r.employee_id} value={r.employee_id}>{r.name}</option>)}</select></label>
      <label><span>Document</span><select value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value })}>{DOC_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
      <label className="ppl-form--wide"><span>Note <em>optional</em></span><input type="text" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="e.g. front and back, before Friday" /></label>
      <div className="ppl-form--wide ppl-actions"><button type="submit" className="ops-btn" disabled={saving}>{saving ? "Sending…" : "Send the request"}</button><span className="ppl-muted">They get a link by email and push; the upload lands in their Docs tab.</span></div>
    </form>
  );
}

/* ── checklists per role: build from the standard set, then edit ── */
function ChecklistsPanel({ templates, sops, credTypes, onChange }) {
  const [editing, setEditing] = useState(null); // template id or 'new'
  const [seeding, setSeeding] = useState(false);
  const seed = async () => {
    setSeeding(true);
    try {
      const defs = defaultTemplates({ sopSlugs: sops.map((s) => s.slug), credentialTypes: credTypes });
      for (const t of defs) await createOnboardingTemplate(t);
      toast.success("Standard checklists created — edit them to fit."); await onChange();
    } catch (e) { toast.error(e?.payload?.detail || "Couldn’t create the checklists."); }
    finally { setSeeding(false); }
  };
  return (
    <div className="ob-panel">
      <div className="ppl-row-head"><div className="ob-sub-title">Checklists by role</div><div className="ppl-actions">{templates.length === 0 && <button type="button" className="ops-btn" disabled={seeding} onClick={seed}>{seeding ? "Creating…" : "Create the standard checklists"}</button>}<button type="button" className="ops-btn secondary" onClick={() => setEditing("new")}>New checklist</button></div></div>
      {templates.length === 0 && editing !== "new" && <div className="ppl-muted">None yet. The standard set is Technician, Office and Sales: I-9, W-4, direct deposit, driver’s license, the handbook and safety policy to sign (if published), the licenses your state requires, and a first training.</div>}
      {editing === "new" && <TemplateEditor sops={sops} credTypes={credTypes} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await onChange(); }} />}
      {templates.map((t) => (
        <div key={t.id} className="ob-template">
          {editing === t.id ? <TemplateEditor template={t} sops={sops} credTypes={credTypes} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await onChange(); }} />
            : (
              <>
                <div className="ppl-row-head"><div><span className="ppl-name">{t.name}</span><span className="ppl-sub"> · {TEMPLATE_ROLES.find((r) => r.value === t.role)?.label || t.role} · {t.steps.length} step{t.steps.length === 1 ? "" : "s"}</span></div>
                  <div className="ppl-actions"><button type="button" className="ppl-link" onClick={() => setEditing(t.id)}>Edit</button><button type="button" className="ppl-link" onClick={async () => { if (!window.confirm(`Retire “${t.name}”? People already on it keep their steps.`)) return; try { await deleteOnboardingTemplate(t.id); await onChange(); } catch { toast.error("Couldn’t retire it."); } }}>Retire</button></div></div>
                <ol className="ob-template-steps">{t.steps.map((s) => <li key={s.id}><span>{s.label}</span><span className="ppl-sub">{KIND_LABEL[s.kind] || s.kind}{s.assignee === "manager" ? " · manager" : ""}</span></li>)}</ol>
              </>
            )}
        </div>
      ))}
    </div>
  );
}

function TemplateEditor({ template, sops, credTypes, onClose, onSaved }) {
  const [name, setName] = useState(template?.name || "");
  const [role, setRole] = useState(template?.role || "tech");
  const [steps, setSteps] = useState(() => (template?.steps || []).map((s) => ({ kind: s.kind, label: s.label, assignee: s.assignee || "employee", required: s.required !== false, config: { ...(s.config || {}) } })));
  const [saving, setSaving] = useState(false);
  const set = (i, patch) => setSteps((st) => st.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const setCfg = (i, patch) => setSteps((st) => st.map((s, j) => (j === i ? { ...s, config: { ...s.config, ...patch } } : s)));
  const move = (i, d) => setSteps((st) => { const n = [...st]; const j = i + d; if (j < 0 || j >= n.length) return st; [n[i], n[j]] = [n[j], n[i]]; return n; });
  const add = (kind) => setSteps((st) => [...st, { kind, label: kind === "document_upload" ? "Upload your …" : kind === "signature" ? "Read and sign …" : kind === "credential" ? "License on file" : "Training", assignee: kind === "credential" || kind === "training" ? "manager" : "employee", required: true, config: {} }]);
  const save = async () => {
    if (!name.trim()) { toast.error("Name the checklist."); return; }
    if (steps.some((s) => !s.label.trim())) { toast.error("Every step needs a label."); return; }
    setSaving(true);
    try {
      const body = { name: name.trim(), role, steps: steps.map((s, i) => ({ ...s, order: i })) };
      if (template) await updateOnboardingTemplate(template.id, body); else await createOnboardingTemplate(body);
      toast.success("Checklist saved"); await onSaved();
    } catch (e) { toast.error(e?.payload?.detail || "Couldn’t save."); }
    finally { setSaving(false); }
  };
  return (
    <div className="ob-editor">
      <div className="ppl-form">
        <label><span>Name</span><input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Field technician" /></label>
        <label><span>For role</span><select value={role} onChange={(e) => setRole(e.target.value)}>{TEMPLATE_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}</select></label>
      </div>
      <div className="ob-editor-steps">
        {steps.map((s, i) => (
          <div key={i} className="ob-editor-step">
            <span className="ob-editor-n">{i + 1}</span>
            <select value={s.kind} onChange={(e) => set(i, { kind: e.target.value, config: {} })}>{STEP_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}</select>
            <input type="text" value={s.label} onChange={(e) => set(i, { label: e.target.value })} placeholder="Step label" />
            {s.kind === "document_upload" && <select value={s.config.doc_type || ""} onChange={(e) => setCfg(i, { doc_type: e.target.value })}><option value="">Document type…</option>{DOC_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>}
            {s.kind === "signature" && <select value={s.config.sop_slug || ""} onChange={(e) => setCfg(i, { sop_slug: e.target.value })}><option value="">Which SOP…</option>{sops.map((x) => <option key={x.slug} value={x.slug}>{x.title}</option>)}</select>}
            {s.kind === "credential" && <select value={s.config.credential_type_key || ""} onChange={(e) => setCfg(i, { credential_type_key: e.target.value })}><option value="">Which license…</option>{credTypes.map((c) => <option key={c.type_key} value={c.type_key}>{c.label || c.type_key}</option>)}</select>}
            {s.kind === "training" && <input type="text" value={s.config.training_course || ""} onChange={(e) => setCfg(i, { training_course: e.target.value })} placeholder="Course name" />}
            <select value={s.assignee} onChange={(e) => set(i, { assignee: e.target.value })}><option value="employee">Employee</option><option value="manager">Manager</option></select>
            <span className="ob-editor-tools"><button type="button" className="ppl-link" onClick={() => move(i, -1)} title="Move up">↑</button><button type="button" className="ppl-link" onClick={() => move(i, 1)} title="Move down">↓</button><button type="button" className="ppl-link" onClick={() => setSteps((st) => st.filter((_, j) => j !== i))} title="Remove">✕</button></span>
          </div>
        ))}
      </div>
      <div className="ppl-actions ob-editor-foot">
        <span className="ppl-sub">Add:</span>{STEP_KINDS.map((k) => <button key={k.value} type="button" className="ppl-link" onClick={() => add(k.value)}>{k.label}</button>)}
        <span style={{ flex: 1 }} />
        <button type="button" className="ops-btn secondary" onClick={onClose}>Cancel</button>
        <button type="button" className="ops-btn" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save checklist"}</button>
      </div>
    </div>
  );
}
